import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  Renderer2,
  WritableSignal,
  afterNextRender,
  computed,
  inject,
  signal, ViewChild, OnDestroy,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from "@angular/common";
import { FooterComponent } from "../shared/footer/footer.component";
import { RouterLink } from "@angular/router";
import { FirebaseCrudService } from '../shared/firebase-crud.service';
import { ResolutionService } from '../shared/resolution.service';
import { SignalsStoreService } from '../shared/signals-store.service';
import { DomSanitizer, Meta, SafeHtml } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { CanonicalService } from '../shared/canonical.service';

import { gsap } from 'gsap'
import { ProducersService } from '../pages/producers/producers.service';
import { handleImageError } from '../shared/common/utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { YoutubeVideoComponent } from '../shared/modal-content/youtube-video/youtube-video.component';


@Component({
    selector: 'app-home',
    imports: [CommonModule, FooterComponent, RouterLink],
    templateUrl: './home.component.html',
    styleUrl: './home.component.scss',
    host: { 'class': 'home-page' }
})
export class HomeComponent implements OnInit, OnDestroy {

  private resolutionService = inject(ResolutionService);
  producersService = inject(ProducersService);
  private signalsStoreService = inject(SignalsStoreService);
  private meta = inject(Meta);
  #canonicalService = inject(CanonicalService);
  #document = inject(DOCUMENT);
  private sanitizer = inject(DomSanitizer);
  private modalService = inject(NgbModal);
  handleImageError = handleImageError;

  readonly heroVideo = environment.config.homeHeroVideo;

  // Set after hydration: null during SSR/first paint (poster only), then the
  // rendition matching the viewport — or kept null under prefers-reduced-motion.
  readonly heroVideoSources = signal<{ webm: string; mp4: string } | null>(null);

  hasSession = computed(() => this.signalsStoreService.hasSession());

  openHeroFilm(): void {
    const modalRef = this.modalService.open(YoutubeVideoComponent, {
      centered: true,
      size: 'xl',
      modalDialogClass: 'youtube-video-dialog'
    });
    modalRef.componentInstance.videoId = this.heroVideo.youtubeFilmId;
    modalRef.componentInstance.videoTitle = 'The Produce Box — Watch the 1-Minute Film';
  }

  popularCategories = [
    {
      img: 'assets/images/landing/home/category/1.png',
      text: 'Fresh Produce',
      link: '/shop/produce'
    },
    {
      img: 'assets/images/landing/home/category/2.png',
      text: 'Local Meat',
      link: '/shop/meat'
    },
    {
      img: 'assets/images/landing/home/category/3.png',
      text: 'Bakery',
      link: '/shop/bakery'
    },
    {
      img: 'assets/images/landing/home/category/4.webp',
      text: 'Dairy & Eggs',
      link: '/shop/dairy-and-eggs'
    },
    {
      img: 'assets/images/landing/home/category/5.png',
      text: 'Prepared Meals',
      link: '/shop/prepared'
    },
    {
      img: 'assets/images/landing/home/category/6.png',
      text: 'Produce Boxes',
      link: '/shop/produce-boxes'
    },
  ]

  locations = [
    {
      text: '',
      color: '#92519e'
    },
    {
      text: 'Raleigh',
      color: '#92519e'
    },
    {
      text: 'Durham',
      color: '#EFCF16'
    },
    {
      text: 'Chapel Hill',
      color: '#F17441'
    },
    {
      text: 'Wake Forest',
      color: '#3b4a91'
    },
    {
      text: 'Apex',
      color: '#c87877'
    },
    {
      text: 'Clayton',
      color: '#69975a'
    },
    {
      text: 'Fuquay-Varina',
      color: '#92519e'
    },
    {
      text: 'Hillsborough',
      color: '#ba894b'
    }
  ];
  intervalId: any;
  @ViewChild('textAnime', { static: false }) textAnime!: ElementRef<HTMLHeadingElement>;
  @ViewChild('heroVideoEl', { static: false }) heroVideoEl?: ElementRef<HTMLVideoElement>;

  sections: HTMLElement[] = [];

  videoUrl: WritableSignal<string> = signal('');

  isMobile = computed(() => this.resolutionService.isMobile())

  producersCards = computed(() => {

    const producers = this.producersService.producersSignal();

    if (!producers)
      return [];

    return producers
      .slice(0, 3)
      .map((p: any) => {
        const desc = (p.description || '');
        const truncatedDescription = this.truncateHtml(desc, 100, p.detailUrl, p.name);
        p.description = truncatedDescription;
        return p
      });
  });

  hasGlobalMessages = computed(() => !!this.signalsStoreService.globalMessages().length);

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: any) {
    afterNextRender(() => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const isMobile = window.matchMedia('(max-width: 767.98px)').matches;
      this.heroVideoSources.set(
        isMobile
          ? this.heroVideo.mobile
          : { webm: this.heroVideo.webm, mp4: this.heroVideo.mp4 }
      );
      // The <source> children render on the next CD cycle; re-run resource
      // selection so the already-parsed video picks them up and autoplays.
      setTimeout(() => {
        const video = this.heroVideoEl?.nativeElement;
        if (!video) return;
        video.load();
        video.play().catch(() => { /* autoplay may be blocked; poster remains */ });
      });
    });
  }

  ngOnInit() {
    this.setUpSEOTags();
    // Retrieve all sections
    const sectionElements = this.elementRef.nativeElement.querySelectorAll('.has-animation');
    this.sections = Array.from(sectionElements);
    this.stopInterval();

    if (isPlatformBrowser(this.platformId))
      this.producersService.getAllProducers();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    // If interval is not already running, start it
    if (!this.intervalId) {
      this.startInterval();
    }
  }
  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent) {
    const touch = event.touches[0];
    if (touch) {
      if (!this.intervalId) {
        this.startInterval();
      }
    }
  }

  startInterval(): void {
    var item = 1;
    this.intervalId = setInterval(() => {
      this.textChange(item);
      item = item + 1;
      if (item == this.locations.length) item = 1;
    }, 1000); // Run every 1 second while mouse is moving
  }
  stopInterval(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId); // Stop interval
      this.intervalId = null;
    }
  }
  ngOnDestroy(): void {
    this.stopInterval();
    this.#document.querySelector('script[data-seo="organization-jsonld"]')?.remove();
  }
  textChange(item: number): void {
    gsap.to(this.textAnime.nativeElement, {
      innerHTML: this.locations[item].text,
      color: this.locations[item].color,
    });
  }



  @HostListener('scroll', ['$event'])
  onElementScroll(event: any) {
    const scrollPosition = event.target.scrollTop;
    const windowHeight = event.target.clientHeight;
    // Loop through each section
    this.sections.forEach(section => {
      const sectionOffset = section.offsetTop;
      const sectionHeight = section.offsetHeight;

      // Check if the section is in the viewport
      if (scrollPosition >= sectionOffset - windowHeight && scrollPosition < sectionOffset + sectionHeight) {
        // Add a class to the section
        this.renderer.addClass(section, 'animated');
      } else {
        // Remove the class from the section
        this.renderer.removeClass(section, 'animated');
      }
    });
  }

  private setUpSEOTags() {
    const { mainTitle, mainDescription, baseKeywords: keywords } = environment.config.seo;

    // Page title + og:title + twitter:title
    this.#canonicalService.setPageTitle(mainTitle);

    // Standard <meta> tags for SEO
    this.meta.updateTag({ name: 'description', content: mainDescription });
    this.meta.updateTag({ name: 'keywords', content: keywords.join(', ') });

    // Open Graph description
    this.meta.updateTag({ property: 'og:description', content: mainDescription });

    // Ensure og:type resets to 'website' when navigating from product pages
    this.#canonicalService.setPageType('website');

    // Organization structured data
    this.#setOrganizationJsonLd();
  }

  #setOrganizationJsonLd() {
    const existing = this.#document.querySelector('script[data-seo="organization-jsonld"]');
    if (existing) return;

    const script = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'organization-jsonld');
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: environment.application.name,
      url: environment.config.seo.baseURL,
      logo: `${environment.config.seo.baseURL}assets/images/logo.webp`,
      description: environment.config.seo.mainDescription,
      contactPoint: {
        '@type': 'ContactPoint',
        email: environment.config.contactEmail,
        contactType: 'customer service'
      }
    });
    this.renderer.appendChild(this.#document.head, script);
  }

  truncateHtml(html: string, maxLength: number, url: string, producerName: string = ''): SafeHtml {
    if (!isPlatformBrowser(this.platformId)) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    let charCount = 0;
    const truncateNode = (node: Node): boolean => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textContent = node.textContent || '';
        if (charCount + textContent.length > maxLength) {
          const truncatedText = textContent.slice(0, maxLength - charCount) + '... ';
          node.textContent = truncatedText;
          return true;
        } else {
          charCount += textContent.length;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const childNodes = Array.from(node.childNodes);
        for (const childNode of childNodes) {
          if (truncateNode(childNode)) {
            return true;
          }
        }
      }
      return false;
    };

    truncateNode(tempDiv);

    const linkText = producerName ? `Read more about ${producerName}` : 'Read more about this producer';
    let truncatedHtml = tempDiv.innerHTML;
    if (tempDiv.innerHTML.includes('</p>'))
      truncatedHtml = tempDiv.innerHTML.replace('</p>', `<a href="/pages/producers/${url}" class="fw-bold"> ${linkText}</a></p>`);
    else
      truncatedHtml = tempDiv.innerHTML += `<a href="/pages/producers/${url}" class="fw-bold"> ${linkText}</a></p>`;
    return this.sanitizer.bypassSecurityTrustHtml(truncatedHtml);
  }
}


